import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Badge } from "../components/Badge";
import { Button } from "../components/Button";
import { EmptyState } from "../components/EmptyState";
import { Spinner } from "../components/Spinner";
import { StatusDot, callStatusTone } from "../components/StatusDot";
import { Table, TBody, TD, TH, THead, TR } from "../components/Table";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  formatDuration,
  formatPhone,
  formatRelative,
  greetingForNow,
} from "../lib/format";
import type { AgentSummary, CallSummary } from "../lib/types";

const DIRECTION_LABEL: Record<CallSummary["direction"], string> = {
  inbound: "In",
  outbound: "Out",
  browser_test: "Test",
};

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [calls, setCalls] = useState<CallSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    Promise.all([
      api.agents.list(controller.signal),
      api.calls.list({ page: 1, page_size: 8 }, controller.signal),
    ])
      .then(([agentList, callPage]) => {
        setAgents(agentList);
        setCalls(callPage.items);
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : "Could not load your dashboard.");
      });
    return () => controller.abort();
  }, [reloadKey]);

  const agentNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const agent of agents ?? []) map.set(agent.id, agent.name);
    return map;
  }, [agents]);

  const loading = (agents === null || calls === null) && !error;
  const activeCount = (agents ?? []).filter((a) => a.status === "active").length;

  return (
    <div className="flex flex-col gap-8">
      {/* Greeting + primary CTA */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-xl tracking-display text-bright">
            {greetingForNow(user?.name)}
          </h1>
          <p className="mt-1 text-sm text-muted">
            {agents === null
              ? "Loading your agents…"
              : agents.length === 0
                ? "No agents yet — create your first one."
                : `${agents.length} agent${agents.length === 1 ? "" : "s"}, ${activeCount} active.`}
          </p>
        </div>
        <Button variant="primary" onClick={() => navigate("/agents/new")}>
          Create agent
        </Button>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded border border-danger/60 bg-danger/10 px-4 py-3">
          <p className="text-sm text-danger">{error}</p>
          <Button size="sm" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </Button>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-16">
          <Spinner size="lg" />
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Stat strip */}
          <div className="grid grid-cols-2 gap-px overflow-hidden rounded border border-line bg-line sm:grid-cols-4">
            <Stat label="Agents" value={agents?.length ?? 0} />
            <Stat label="Active" value={activeCount} />
            <Stat label="Recent calls" value={calls?.length ?? 0} />
            <Stat
              label="Live now"
              value={(calls ?? []).filter((c) => c.status === "in_progress").length}
            />
          </div>

          {/* Recent calls */}
          <section className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between">
              <h2 className="font-display text-lg tracking-display text-bright">Recent calls</h2>
              <Link
                to="/calls"
                className="text-xs text-muted underline underline-offset-4 transition-colors hover:text-bright"
              >
                View all
              </Link>
            </div>

            {calls && calls.length === 0 ? (
              <EmptyState
                title="No calls yet"
                description="Create an agent and talk to it right in your browser — no phone number needed."
                action={
                  (agents?.length ?? 0) === 0 ? (
                    <Button variant="primary" onClick={() => navigate("/agents/new")}>
                      Create agent
                    </Button>
                  ) : (
                    <Button onClick={() => navigate("/agents")}>Open your agents</Button>
                  )
                }
              />
            ) : (
              <Table>
                <THead>
                  <TR>
                    <TH>Direction</TH>
                    <TH>Agent</TH>
                    <TH>Number</TH>
                    <TH>Status</TH>
                    <TH align="right">Duration</TH>
                    <TH align="right">Started</TH>
                  </TR>
                </THead>
                <TBody>
                  {(calls ?? []).map((call) => (
                    <TR key={call.id} interactive onClick={() => navigate(`/calls/${call.id}`)}>
                      <TD>
                        <Badge mono tone={call.direction === "browser_test" ? "default" : "bright"}>
                          {DIRECTION_LABEL[call.direction]}
                        </Badge>
                      </TD>
                      <TD className="text-bright">
                        {agentNames.get(call.agent_id) ?? "—"}
                      </TD>
                      <TD mono>
                        {call.direction === "browser_test"
                          ? "browser"
                          : formatPhone(call.direction === "outbound" ? call.to_number : call.from_number)}
                      </TD>
                      <TD>
                        <StatusDot
                          tone={callStatusTone(call.status)}
                          label={call.status.replace("_", " ")}
                          pulse={call.status === "in_progress"}
                        />
                      </TD>
                      <TD align="right" mono>
                        {formatDuration(call.duration_seconds)}
                      </TD>
                      <TD align="right" mono>
                        {formatRelative(call.started_at)}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1 bg-surface px-4 py-3">
      <span className="text-xs uppercase tracking-wide text-muted">{label}</span>
      <span className="font-display text-xl tracking-display text-bright">{value}</span>
    </div>
  );
}
